import { useState } from 'react';
import { ExternalLink, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Grid cell that resolves a domain's most recent deal on click and opens it in
 * a new tab. Lookup is on demand (one API call per click), so rows carry no
 * deal data up front. Pair with `useRecentDealByDomain().getRecentDeal`.
 */
export function RecentDealLink({
  domain,
  getRecentDeal,
}: {
  domain: string;
  getRecentDeal: (domain: string) => Promise<{ id: string } | null>;
}) {
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsLoading(true);
    try {
      const deal = await getRecentDeal(domain);
      if (deal) {
        window.open(`/app/deals/${deal.id}`, '_blank');
      } else {
        toast.error('No deal found for this domain');
      }
    } catch {
      toast.error('Failed to find deal');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isLoading}
      className="inline-flex items-center text-blue-600 hover:underline"
      title="Open recent deal in new tab"
    >
      {isLoading
        ? (
            <>
              <Loader2 className="mr-1 size-3 animate-spin" />
              Loading
            </>
          )
        : (
            <>
              View
              <ExternalLink className="ml-1 size-3" />
            </>
          )}
    </button>
  );
}
